// Direct S3 upload service using presigned URLs for large files

export interface DirectS3UploadOptions {
  folder: 'previews' | 'originals';
  contentType?: string;
  onProgress?: (progress: number) => void;
}

export interface DirectS3UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

// Get presigned upload URL from Netlify function
const getPresignedUrl = async (
  fileName: string, 
  folder: string, 
  contentType: string,
  fileSize: number
): Promise<{success: boolean; uploadUrl?: string; publicUrl?: string; key?: string; error?: string}> => {
  try {
    const response = await fetch('/.netlify/functions/get-upload-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName,
        folder,
        contentType,
        fileSize
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get presigned URL'
    };
  }
};

// Upload file directly to S3 using presigned URL
export const uploadToS3Direct = async (
  file: File,
  fileName: string,
  options: DirectS3UploadOptions
): Promise<DirectS3UploadResult> => {
  try {
    console.log(`Starting direct S3 upload: ${fileName}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    
    // Get presigned URL
    const presignedResult = await getPresignedUrl(
      fileName, 
      options.folder, 
      options.contentType || file.type,
      file.size
    );

    if (!presignedResult.success || !presignedResult.uploadUrl) {
      throw new Error(presignedResult.error || 'Failed to get presigned URL');
    }

    console.log(`Presigned URL obtained for: ${fileName}`);

    // Upload file directly to S3 using presigned URL
    // Include Content-Type header to match what was signed
    const uploadResponse = await fetch(presignedResult.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': options.contentType || file.type,
      },
    });

    console.log(`S3 upload response status: ${uploadResponse.status} ${uploadResponse.statusText}`);

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}: ${errorText}`);
    }

    console.log(`Direct S3 upload completed successfully: ${fileName}`);

    return {
      success: true,
      url: presignedResult.publicUrl,
      key: presignedResult.key
    };

  } catch (error) {
    console.error('Direct S3 upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed'
    };
  }
};


// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};